import { useCallback, useMemo, useState } from 'react';
import styles from './hsaCreatePassword.module.scss';
import ParentAuthPageLayout from '../../../components/auth-page-layout/HsaAuthPageLayout';
import { TextInput, TextInputValue } from '../../../components/text-input/TextInput';
import { useQueryParams } from '../../../hooks/useQueryParams';
import { useHistory } from 'react-router-dom';
import { validatePassword } from '../../../utils/validation';
import Button from 'src/components/button/Button';
import { useHeaSetPasswordMutation } from '@src/api/graphql-codegen';
import Spinner from '@components/spinner/Spinner';

type Props = {};

function CreatePassword(props: Props) {
  const token = useQueryParams().get('token');
  const { push } = useHistory();

  const [clientError, setClientError] = useState<undefined | string>();
  const [completed, setCompleted] = useState(false);

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [heaSetPassword, { loading, error }] = useHeaSetPasswordMutation({
    onCompleted: () => {
      push('/');
    },
    onError: (e) => {
      const errorMessage = e?.message || e?.graphQLErrors?.[0]?.message;
      setClientError(errorMessage);
      setCompleted(false);
    }
  });

  const handleChange = useCallback((setter) => (value: TextInputValue) => {
    setClientError(undefined);
    setter(value);
  }, []);

  const handleSubmit = useCallback(async () => {
    const error = validatePassword(newPassword);
    if (error) {
      return setClientError(error);
    }

    if (newPassword !== confirmPassword) {
      return setClientError('The password and the confirm password should match.')
    }
    setClientError(undefined);

    if (token) {
      await heaSetPassword({
        variables: {
          input: {
            token,
            password: newPassword
          }
        }
      });
    }
  }, [newPassword, confirmPassword, heaSetPassword, token]);

  const completedContent = useMemo(() => () => {
    if (!completed) return null;
    if (error) {
      return (
        <div className={styles.confirm}>
          <div className={styles.confirmErrorTitle}>Error!</div>
          <div className={styles.confirmSubtitle}>{error.message}</div>
        </div>
      );
    }
    return (
      <div className={styles.confirm}>
        <div className={styles.confirmTitle}>Your password has been set successfully!</div>
        <div/>
      </div>
    )
  }, [completed, error]);

  return (
    <ParentAuthPageLayout title={'Create a password for your account:'}>
      {completed ? completedContent :
        <>
          <TextInput allowSpecialCharacters type={'password'} className={styles.input} placeholder={'Type your password'} label={''}
                     id={'password'}
                     value={newPassword} onChange={handleChange(setNewPassword)}/>
          <TextInput allowSpecialCharacters type={'password'} className={styles.input} placeholder={'Re-type your password'}
                     label={''}
                     id={'confirmPassword'} value={confirmPassword} onChange={handleChange(setConfirmPassword)}/>
          {!!clientError && <div className={styles.error}>{clientError}</div>}
          <Button disabled={loading} size="small" className={styles.submit} onClick={handleSubmit} label={'Create password'} leftIcon={loading ? <Spinner width={20} height={20} /> : undefined}/>
        </>
      }
    </ParentAuthPageLayout>
  );
}

export default CreatePassword;
